/**
 * What TikTok requires the host to see and confirm before a direct post: their own account, the
 * post itself with editable words, who can see it, what people may do with it, whether it
 * promotes anyone, and the consent sentence. Nothing here is chosen for them.
 */
import { mediaStyle } from '@/lib/scenes';
import {
  nextTikTokDraft,
  tiktokConsent,
  tiktokLabelNote,
  tiktokOffline,
  tiktokPrivacyChoices,
  type TikTokCreator,
  type TikTokDraft,
} from '@/lib/tiktok-post';

type Props = {
  accountName: string;
  creator: TikTokCreator | undefined;
  loading: boolean;
  failed: boolean;
  /** TikTok's own reason when it refused the account (a daily limit, a ban), if it gave one. */
  failedWhy?: string | null;
  /** Duet and Stitch do not apply to photo posts, so TikTok wants them left out. */
  photoPost: boolean;
  previewUrl: string | null;
  /** The words as they will be posted; the host may change them here. */
  words: string;
  onWords: (next: string) => void;
  /** Saves the words once the host leaves the field, like the composer's caption editor. */
  onWordsCommit?: () => void;
  /** The hashtags as typed. They go out with the post, so the host sees them here. */
  hashtags: string;
  /** Absent where the caller cannot save them; then they are shown, not edited. */
  onHashtags?: (next: string) => void;
  onHashtagsCommit?: () => void;
  /** The post's words carry the tracked link, which TikTok does not get. */
  linkLeftOut?: boolean;
  draft: TikTokDraft;
  onDraft: (next: TikTokDraft) => void;
  /** Why the post cannot go yet, or null. */
  blocker: string | null;
};

const box: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
  padding: '14px 16px',
  borderRadius: 14,
  border: '1px solid rgba(0,0,0,.1)',
  background: '#faf8f4',
};
const note: React.CSSProperties = { fontSize: 11.5, color: '#666', lineHeight: 1.45 };
const row: React.CSSProperties = { display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 };

export function TikTokPost({
  accountName,
  creator,
  loading,
  failed,
  failedWhy,
  photoPost,
  previewUrl,
  words,
  onWords,
  onWordsCommit,
  hashtags,
  onHashtags,
  onHashtagsCommit,
  linkLeftOut,
  draft,
  onDraft,
  blocker,
}: Props) {
  // Every change goes through the rule, so consent is never inherited from an older shape.
  const set = (next: Partial<TikTokDraft>) => onDraft(nextTikTokDraft(draft, next));
  const offline = creator ? tiktokOffline(creator) : false;
  const choices = creator ? tiktokPrivacyChoices(creator, draft) : [];
  const whyNot = choices.find((c) => c.why)?.why ?? null;
  const label = tiktokLabelNote(draft);
  const consent = tiktokConsent(draft);
  const interactions = photoPost
    ? ([['Allow comments', 'allowComment', creator?.commentDisabled]] as const)
    : ([
        ['Allow comments', 'allowComment', creator?.commentDisabled],
        ['Allow Duet', 'allowDuet', creator?.duetDisabled],
        ['Allow Stitch', 'allowStitch', creator?.stitchDisabled],
      ] as const);

  return (
    <div style={box} role="group" aria-label={`Posting to TikTok as ${accountName}`}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        {creator?.avatarUrl && (
          <span
            aria-hidden
            style={{
              width: 28,
              height: 28,
              borderRadius: 99,
              ...mediaStyle({ url: creator.avatarUrl }),
            }}
          />
        )}
        <span style={{ fontSize: 13.5, fontWeight: 700 }}>Posting to TikTok as {accountName}</span>
      </div>
      {loading && (
        <span style={{ fontSize: 12.5, color: '#777' }}>
          Asking TikTok what this account allows…
        </span>
      )}
      {failed && (
        <span style={{ fontSize: 12.5, color: '#a33a2a', lineHeight: 1.45 }}>
          {failedWhy ??
            'TikTok did not answer, so we cannot show your posting options. Remove TikTok from this post, or try again in a moment.'}
        </span>
      )}
      {offline && (
        <span style={{ fontSize: 12.5, color: '#a33a2a', lineHeight: 1.45 }}>
          TikTok is not accepting posts from this account at the moment. You can try again later, or
          take TikTok off this post.
        </span>
      )}
      {creator && !offline && (
        <>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
            {previewUrl && (
              <span
                aria-hidden
                style={{
                  width: 54,
                  height: 72,
                  borderRadius: 8,
                  flex: 'none',
                  ...mediaStyle({ url: previewUrl }),
                }}
              />
            )}
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1 }}>
              <span className="label-caps">What it says</span>
              <textarea
                aria-label="What it says"
                value={words}
                rows={3}
                onChange={(e) => onWords(e.target.value)}
                onBlur={() => onWordsCommit?.()}
                style={{
                  font: 'inherit',
                  fontSize: 13,
                  lineHeight: 1.45,
                  padding: '8px 10px',
                  borderRadius: 10,
                  border: '1px solid rgba(0,0,0,.12)',
                  background: '#fff',
                  resize: 'vertical',
                }}
              />
            </label>
          </div>
          {onHashtags ? (
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span className="label-caps">Hashtags</span>
              <input
                aria-label="Hashtags"
                value={hashtags}
                onChange={(e) => onHashtags(e.target.value)}
                onBlur={() => onHashtagsCommit?.()}
                style={{
                  font: 'inherit',
                  fontSize: 13,
                  padding: '8px 10px',
                  borderRadius: 10,
                  border: '1px solid rgba(0,0,0,.12)',
                  background: '#fff',
                }}
              />
            </label>
          ) : (
            hashtags && <span style={note}>{hashtags}</span>
          )}
          {linkLeftOut && (
            <span style={note}>
              Your link stays off TikTok, which does not allow apps to add links to a post.
            </span>
          )}

          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span className="label-caps">Who can see this</span>
            <select
              aria-label="Who can see this"
              value={draft.privacy ?? ''}
              onChange={(e) => set({ privacy: (e.target.value || null) as TikTokDraft['privacy'] })}
              title={whyNot ?? undefined}
            >
              <option value="" disabled>
                Choose who can see this
              </option>
              {choices.map((c) => (
                <option key={c.value} value={c.value} disabled={c.disabled}>
                  {c.label}
                </option>
              ))}
            </select>
            {whyNot && <span style={note}>{whyNot}</span>}
          </label>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span className="label-caps">What people can do with it</span>
            {interactions.map(([text, key, off]) => (
              <label
                key={key}
                style={{
                  ...row,
                  color: off ? '#999' : '#111',
                  cursor: off ? 'default' : 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={draft[key] && !off}
                  disabled={!!off}
                  onChange={(e) => set({ [key]: e.target.checked })}
                />
                {text}
                {off ? ' · turned off on your TikTok account' : ''}
              </label>
            ))}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <label style={{ ...row, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={draft.disclose}
                onChange={(e) =>
                  set(
                    e.target.checked
                      ? { disclose: true }
                      : { disclose: false, yourBrand: false, brandedContent: false },
                  )
                }
              />
              This post promotes a brand, product or service
            </label>
            {draft.disclose && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, paddingLeft: 22 }}>
                <label style={{ ...row, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={draft.yourBrand}
                    onChange={(e) => set({ yourBrand: e.target.checked })}
                  />
                  Your brand · you are promoting your own place
                </label>
                <label style={{ ...row, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={draft.brandedContent}
                    onChange={(e) =>
                      set({
                        brandedContent: e.target.checked,
                        // A paid partnership may not be private, so that choice goes back.
                        ...(e.target.checked && draft.privacy === 'SELF_ONLY'
                          ? { privacy: null }
                          : {}),
                      })
                    }
                  />
                  Branded content · a paid partnership with someone else
                </label>
                <span style={note}>
                  {label ?? 'Pick at least one so TikTok can label the post correctly.'}
                </span>
              </div>
            )}
          </div>

          <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={draft.consented}
              onChange={(e) => set({ consented: e.target.checked })}
              style={{ marginTop: 3, flex: 'none' }}
            />
            <span style={{ ...note, color: '#111' }}>
              {consent.text
                .split(/(Branded Content Policy|Music Usage Confirmation)/)
                .map((part, i) => {
                  const link = consent.links.find((l) => l.label === part);
                  return link ? (
                    <a key={i} href={link.href} target="_blank" rel="noreferrer">
                      {part}
                    </a>
                  ) : (
                    <span key={i}>{part}</span>
                  );
                })}
            </span>
          </label>
          {blocker && (
            <span style={{ fontSize: 12.5, color: '#a33a2a', lineHeight: 1.45 }}>{blocker}</span>
          )}
          {photoPost && (
            <span style={note}>
              TikTok adds recommended music to photo posts. You can change it in TikTok afterwards.
            </span>
          )}
          <span style={note}>
            After you post, it can take a few minutes for TikTok to process it and show it on your
            profile.
          </span>
        </>
      )}
    </div>
  );
}
